/**
 * Calculate implementation group scores for CIS standards
 * @param {Array} items - Array of processed items
 * @param {number} totalSafeguards - Total number of safeguards
 * @param {Object} standardsResponse - Standards response data
 * @param {Object} qualityData - Optional quality data for mapping CRFS safeguard IDs to CIS control IDs
 * @returns {Object} - Implementation group scores
 */
function calculateImplementationGroupScores(items, totalSafeguards, standardsResponse, qualityData) {
	const igScores = {
		'1': { total: 0, count: 0, controls: new Set(), applicableCount: 0, applicableControls: new Set(), unansweredCount: 0, naCount: 0 },
		'2': { total: 0, count: 0, controls: new Set(), applicableCount: 0, applicableControls: new Set(), unansweredCount: 0, naCount: 0 },
		'3': { total: 0, count: 0, controls: new Set(), applicableCount: 0, applicableControls: new Set(), unansweredCount: 0, naCount: 0 }
	};

	// Helper function to parse priority string and return array of IG numbers
	// e.g., '2,3' returns ['2', '3'], '1,2,3' returns ['1', '2', '3'], '3' returns ['3']
	function parsePriority(priorityString) {
		if (!priorityString) return [];
		return priorityString.split(',').map(p => p.trim()).filter(p => ['1', '2', '3'].includes(p));
	}

	// Determine which CIS standard we're working with (needed before first pass for CRFS assessments)
	// Try to get it from WebAssessmentCharts.currentCisVersion (set in calculateStandardSpecificAverages.js)
	// Otherwise, try to extract from standardsResponse items
	let targetCisStandardId = null;
	if (typeof WebAssessmentCharts !== 'undefined' && WebAssessmentCharts.currentCisVersion) {
		targetCisStandardId = WebAssessmentCharts.currentCisVersion; // e.g., 'CIS-7-1', 'CIS-8-0', 'CIS-8-1'
	} else if (standardsResponse && standardsResponse.CS && standardsResponse.CS.Items && standardsResponse.CS.Items.length > 0) {
		// Fallback: try to determine from the response structure or items
		const firstItem = standardsResponse.CS.Items[0];
		if (firstItem.standardid?.S) {
			targetCisStandardId = firstItem.standardid.S;
		} else if (firstItem.standardversion?.S) {
			targetCisStandardId = firstItem.standardversion.S;
		}
	}

	// First pass: count total applicable safeguards for each implementation group from standards response
	// For CRFS assessments, only count controls that have CRFS safeguard mappings (crfmapping or cccmapping fields)
	// For pure CIS assessments, count all controls
	const cisControlIdsWithMappingsForApplicableCount = new Set();
	
	// Check if we're in a CRFS assessment context by looking for items with CRFS-style safeguard IDs
	const isCrfsAssessment = items.length > 0 && items.some(item => {
		const safeguardId = item.safeguardid?.S;
		return safeguardId && /^[A-Z]{2,4}-\d+$/.test(safeguardId); // Pattern like "PNA-20", "CFG-12", etc.
	});
	
	// Determine the assessment year to know which mapping field to use
	// 2021-2023: use cccmapping
	// 2024-2025: use crfmapping
	let assessmentYear = null;
	let mappingField = 'crfmapping'; // Default to crfmapping (for modern assessments or pure CIS)
	
	if (isCrfsAssessment && items.length > 0) {
		// Try to get year from frameworkversion field in items
		const itemWithYear = items.find(item => item.frameworkversion?.S);
		if (itemWithYear) {
			assessmentYear = itemWithYear.frameworkversion.S;
		} else if (typeof WebAssessmentCharts !== 'undefined' && WebAssessmentCharts.assessmentYear) {
			assessmentYear = WebAssessmentCharts.assessmentYear;
		} else {
			// Default to 2025 if we can't determine (modern assessments)
			assessmentYear = '2025';
		}
		const isModernAssessment = assessmentYear && ['2024', '2025', '2026'].includes(assessmentYear);
		mappingField = isModernAssessment ? 'crfmapping' : 'cccmapping';
		// console.log(`CRFS Assessment detected. Year: ${assessmentYear}, Using mapping field: ${mappingField}`);
	}
	
	if (standardsResponse && standardsResponse.CS && standardsResponse.CS.Items) {
		standardsResponse.CS.Items.forEach(control => {
			if (!control.controlpriority || !control.controlpriority.S) return;
			
			const controlId = control.controlid ? control.controlid.S : null;
			if (!controlId) return;
			
			// For CRFS assessments: only count controls that have the appropriate mapping field
			// For pure CIS assessments: count all controls
			if (isCrfsAssessment) {
				const hasMapping = control[mappingField] && control[mappingField].S && control[mappingField].S.trim();
				
				if (!hasMapping) {
					// This control has no CRFS mapping for this assessment year, skip it (it's not part of this assessment)
					return;
				}
				
				// Track which controls have mappings for later use
				cisControlIdsWithMappingsForApplicableCount.add(controlId);
			}
			
			const controlPriority = control.controlpriority.S;
			const igNumbers = parsePriority(controlPriority);
			
			// Add to all relevant IGs
			igNumbers.forEach(ig => {
				igScores[ig].applicableCount++;
			});
		});
	}

	// console.log('DEBUG - Total Applicable Counts per IG from Standards:', {
	// 	'1': igScores['1'].applicableCount,
	// 	'2': igScores['2'].applicableCount,
	// 	'3': igScores['3'].applicableCount
	// });

	// Build a map from CRFS safeguard IDs to CIS control IDs using crfmapping/cccmapping fields
	// This is needed for CRFS assessments where items have safeguard IDs like "CFG-12" or "PNA-20"
	// but the CIS standards response has control IDs like "7.7"
	// The mapping comes from the crfmapping field (2024/2025) or cccmapping field (2021/2022/2023)
	// NOTE: A single safeguard ID can map to multiple CIS controls, so we store arrays
	const safeguardIdToCisControlIdMap = new Map(); // Map<safeguardId, Array<controlId>>
	
	if (standardsResponse && standardsResponse.CS && standardsResponse.CS.Items) {
		standardsResponse.CS.Items.forEach(control => {
			const controlId = control.controlid ? control.controlid.S : null;
			if (!controlId) return;
			
		// Only use the appropriate mapping field based on assessment year
		// For 2024/2025: use crfmapping
		// For 2021/2022/2023: use cccmapping
		if (control[mappingField] && control[mappingField].S) {
			const mappingString = control[mappingField].S.trim();
			// Split by newlines first, then by spaces within each line
			// Handle both \n and \r\n line breaks
			const mappings = mappingString.split(/\r?\n/).flatMap(line => 
				line.trim().split(/\s+/).filter(m => m)
			);
			mappings.forEach(safeguardId => {
				// Handle multiple controls mapping to the same safeguard ID
				if (!safeguardIdToCisControlIdMap.has(safeguardId)) {
					safeguardIdToCisControlIdMap.set(safeguardId, []);
				}
				safeguardIdToCisControlIdMap.get(safeguardId).push(controlId);
				// Debug: Log mappings for control 7.7
				if (controlId === '7.7') {
					// console.log(`DEBUG - Mapped ${safeguardId} -> ${controlId} from ${mappingField}: "${mappingString}"`);
				}
			});
		}
		});
		
		if (safeguardIdToCisControlIdMap.size > 0) {
			// console.log(`Built safeguard ID to CIS control ID map with ${safeguardIdToCisControlIdMap.size} mappings from ${mappingField} field`);
			// Log first few mappings
			let loggedCount = 0;
			safeguardIdToCisControlIdMap.forEach((cisControlId, safeguardId) => {
				if (loggedCount < 10) {
					// console.log(`Mapped ${safeguardId} -> ${cisControlId}`);
					loggedCount++;
				}
			});
			
			// Debug: Check for controls with multiple mappings
			const controlToSafeguards = new Map();
			safeguardIdToCisControlIdMap.forEach((cisControlId, safeguardId) => {
				if (!controlToSafeguards.has(cisControlId)) {
					controlToSafeguards.set(cisControlId, []);
				}
				controlToSafeguards.get(cisControlId).push(safeguardId);
			});
			
			// Log controls with multiple mappings
			const multiMappedControls = Array.from(controlToSafeguards.entries()).filter(([_, safeguards]) => safeguards.length > 1);
			if (multiMappedControls.length > 0) {
				// console.log(`Found ${multiMappedControls.length} controls with multiple CRFS safeguard mappings:`, multiMappedControls.slice(0, 5));
			}
		}
	}

	// Create a map of all possible safeguards from the standard
	const allSafeguards = new Map();
	// Also create a map of safeguard IDs to control priorities for quick lookup
	// This map will contain multiple keys for the same priority to handle different ID formats:
	// - Full control ID (e.g., "CIS-8-0-EM-08")
	// - Extracted safeguard ID (e.g., "EM-08")
	// - CIS control ID (e.g., "7.7")
	const controlPriorityMap = new Map();
	if (standardsResponse && standardsResponse.CS && standardsResponse.CS.Items) {
		standardsResponse.CS.Items.forEach(control => {
			if (control.controlid && control.controlid.S && control.controlpriority && control.controlpriority.S) {
				const controlId = control.controlid.S;
				const priority = control.controlpriority.S;
				
				allSafeguards.set(controlId, {
					safeguardid: { S: controlId },
					controlpriority: control.controlpriority
				});
				
				// Store control priority with the full control ID
				controlPriorityMap.set(controlId, priority);
				
				// Also try to extract just the safeguard part if the control ID has a prefix
				// For formats like "CIS-8-0-EM-08" -> extract "EM-08"
				// For formats like "EM-08" -> already the safeguard ID
				const idParts = controlId.split('-');
				if (idParts.length > 3) {
					// Likely has prefix like "CIS-8-0-EM-08", extract last two parts "EM-08"
					const extractedId = idParts.slice(-2).join('-');
					if (!controlPriorityMap.has(extractedId)) {
						controlPriorityMap.set(extractedId, priority);
					}
					// Also try with just the last part (e.g., "08") but this might be too generic
				} else if (idParts.length === 2) {
					// Already in format like "EM-08", store it (might already be stored above)
					controlPriorityMap.set(controlId, priority);
				}
			}
		});
	}

	// Second pass: calculate scores from actual implementation data
	items.forEach(item => {
		const safeguardId = item.safeguardid ? item.safeguardid.S : null;
		if (!safeguardId) {
			// console.log(`Skipping item - no safeguard ID`);
			return;
		}

		// Get all CIS control IDs that this safeguard maps to
		// A single safeguard can map to multiple controls (e.g., PNA-20 maps to both 7.7 and 8.7)
		let mappedControlIds = [];
		
		if (safeguardIdToCisControlIdMap.has(safeguardId)) {
			mappedControlIds = safeguardIdToCisControlIdMap.get(safeguardId);
		}
		
		// If no mapping found, check if the safeguard ID is already a valid CIS control ID
		// (This handles pure CIS assessments where safeguardId IS the CIS control ID)
		if (mappedControlIds.length === 0) {
			const controlPriority = controlPriorityMap.get(safeguardId);
			if (controlPriority) {
				mappedControlIds = [safeguardId]; // For pure CIS, safeguardId IS the CIS control ID
			}
		}
		
		// If item already has controlpriority but we don't have a valid CIS control ID,
		// we still need to find the CIS control ID to avoid counting non-CIS safeguards
		if (item.controlpriority && item.controlpriority.S && mappedControlIds.length === 0) {
			// Item has priority but we couldn't map it - this might be a CRFS safeguard without mapping
			// Try to find it in standards response by searching for matching control IDs
			if (standardsResponse && standardsResponse.CS && standardsResponse.CS.Items) {
				for (const control of standardsResponse.CS.Items) {
					if (control.controlid && control.controlid.S && control.controlpriority && control.controlpriority.S) {
						const controlId = control.controlid.S;
						// Check if control ID matches or ends with the safeguard ID
						if (controlId === safeguardId ||
							controlId.endsWith('-' + safeguardId) || 
							controlId.endsWith('_' + safeguardId)) {
							mappedControlIds = [controlId];
							// Store mappings for future use
							controlPriorityMap.set(safeguardId, control.controlpriority.S);
							controlPriorityMap.set(controlId, control.controlpriority.S);
							break;
						}
					}
				}
			}
		}
		
		// If we still don't have any valid CIS control IDs, skip this item
		// This means it's a CRFS safeguard that doesn't map to any CIS control
		if (mappedControlIds.length === 0) {
			// console.log(`Skipping item ${safeguardId} - no CIS control ID mapping found (not a CIS control)`);
			return;
		}
		
		const maturityScore = item.maturityscore; // Can be null for policy status 1
		const policystatus = parseInt(item.policystatus.N, 10);
		const isNotApplicable = policystatus === 1;
		
		// Process this item for EACH control ID it maps to
		// This ensures that if PNA-20 maps to both 7.7 and 8.7, the item is counted for both
		mappedControlIds.forEach(cisControlId => {
			// Validate that the cisControlId exists in the standards response
			// For CRFS assessments, also validate it's one of the controls with mappings
			const controlExistsInStandards = controlPriorityMap.has(cisControlId);
			if (!controlExistsInStandards) {
				// console.log(`Skipping item ${safeguardId} -> ${cisControlId} - CIS control ID not found in standards`);
				return;
			}
			
			// For CRFS assessments, ensure this control has a mapping (is part of the assessment)
			if (isCrfsAssessment && !cisControlIdsWithMappingsForApplicableCount.has(cisControlId)) {
				// console.log(`Skipping item ${safeguardId} -> ${cisControlId} - CIS control has no CRFS mapping (not part of this assessment)`);
				return;
			}
			
			// Get the priority for this control
			let controlPriority = controlPriorityMap.get(cisControlId);
			
			// Use the priority from the item if available, otherwise use what we looked up
			if (!controlPriority && item.controlpriority && item.controlpriority.S) {
				controlPriority = item.controlpriority.S;
			}
			
			// If we still don't have a priority, skip this control
			if (!controlPriority) {
				// console.log(`Skipping item ${safeguardId} -> ${cisControlId} - no control priority found`);
				return;
			}
			
			if (mappedControlIds.length > 1) {
				// console.log(`Processing item ${safeguardId} -> ${cisControlId} (one of ${mappedControlIds.length} controls):`, {
				// 	controlPriority,
				// 	maturityScore,
				// 	isNotApplicable
				// });
			}

			// Determine which implementation group(s) this control belongs to based on priority
			// Priority can be comma-separated (e.g., '2,3' means it belongs to both IG2 and IG3)
			// Use CIS control ID as the key to prevent duplicates
			const igNumbers = parsePriority(controlPriority);
			
			igNumbers.forEach(ig => {
				if (!igScores[ig].controls.has(cisControlId)) {
					igScores[ig].controls.add(cisControlId);
					
					// Count as applicable if not marked as Not Applicable
					if (!isNotApplicable) {
						igScores[ig].applicableControls.add(cisControlId);
						
						// Only add to total if it has a maturity score
						if (maturityScore !== null) {
							igScores[ig].total += maturityScore;
							igScores[ig].count++;
						} else {
							// Count unanswered questions
							igScores[ig].unansweredCount++;
						}
					} else {
						// Count Not Applicable items
						igScores[ig].naCount++;
					}
					// console.log(`Added to IG${ig}: ${cisControlId} (from ${safeguardId}) with score ${maturityScore}, Not Applicable: ${isNotApplicable}`);
				}
			});
		});
	});

	// Count unanswered questions from standards that weren't in the items
	// Use CIS control ID from standards (not CRFS safeguard IDs)
	// IMPORTANT: For CRFS assessments, only count controls that have CRFS safeguard mappings
	// as "unanswered". Controls without mappings are not part of this assessment.
	allSafeguards.forEach((control, cisControlId) => {
		// For CRFS assessments: only process controls that have crfmapping or cccmapping
		// For pure CIS assessments: process all controls
		if (isCrfsAssessment) {
			if (!cisControlIdsWithMappingsForApplicableCount.has(cisControlId)) {
				// This control has no CRFS mapping, skip it (it's not part of this assessment)
				return;
			}
		}
		
		if (!igScores['1'].controls.has(cisControlId) && 
			!igScores['2'].controls.has(cisControlId) && 
			!igScores['3'].controls.has(cisControlId)) {
			
			const controlPriority = control.controlpriority.S;
			const igNumbers = parsePriority(controlPriority);
			
			// Add to unanswered count for all relevant IGs
			igNumbers.forEach(ig => {
				igScores[ig].unansweredCount++;
			});
		}
	});

	// Debug: Find which controls are missing and check if their safeguard IDs are in items
	if (isCrfsAssessment) {
		// Build a Set of all safeguard IDs from items for quick lookup
		const itemSafeguardIds = new Set();
		items.forEach(item => {
			const safeguardId = item.safeguardid?.S;
			if (safeguardId) {
				itemSafeguardIds.add(safeguardId);
			}
		});
		// console.log(`DEBUG - Total items processed: ${items.length}, Unique safeguard IDs in items: ${itemSafeguardIds.size}`);
		
		Object.keys(igScores).forEach(ig => {
			const missingControls = [];
			if (standardsResponse && standardsResponse.CS && standardsResponse.CS.Items) {
				standardsResponse.CS.Items.forEach(control => {
					const controlId = control.controlid ? control.controlid.S : null;
					if (!controlId || !cisControlIdsWithMappingsForApplicableCount.has(controlId)) {
						return; // Skip controls without mappings
					}
					
					const controlPriority = control.controlpriority ? control.controlpriority.S : null;
					if (!controlPriority) return;
					
					const igNumbers = parsePriority(controlPriority);
					if (igNumbers.includes(ig) && !igScores[ig].controls.has(controlId)) {
						// Get all safeguard IDs that map to this control
						// Since the map now stores arrays of control IDs, we need to check if controlId is in the array
						const safeguardIdsForControl = [];
						safeguardIdToCisControlIdMap.forEach((mappedControlIds, safeguardId) => {
							// mappedControlIds is now an array
							if (Array.isArray(mappedControlIds)) {
								if (mappedControlIds.includes(controlId) || mappedControlIds.some(id => String(id) === String(controlId))) {
									safeguardIdsForControl.push(safeguardId);
								}
							} else {
								// Legacy: handle old format where it was a single value (shouldn't happen now)
								if (mappedControlIds === controlId || String(mappedControlIds) === String(controlId)) {
									safeguardIdsForControl.push(safeguardId);
								}
							}
						});
						
						// Check which of these safeguard IDs are in the items
						const foundSafeguards = safeguardIdsForControl.filter(id => itemSafeguardIds.has(id));
						const missingSafeguards = safeguardIdsForControl.filter(id => !itemSafeguardIds.has(id));
						
						missingControls.push({
							controlId,
							priority: controlPriority,
							[mappingField]: control[mappingField]?.S || 'none',
							safeguardIdsForControl,
							foundSafeguards,
							missingSafeguards
						});
					}
				});
			}
			
			if (missingControls.length > 0) {
				// console.log(`DEBUG - IG${ig} Missing Controls (${missingControls.length}):`, missingControls);
			}
		});
	}
	
	// console.log('DEBUG - Implementation Group Totals:', {
	// 	'1': { 
	// 		total: igScores['1'].total, 
	// 		count: igScores['1'].count, 
	// 		applicableCount: igScores['1'].applicableCount,
	// 		applicableControlsCount: igScores['1'].applicableControls.size,
	// 		unansweredCount: igScores['1'].unansweredCount,
	// 		naCount: igScores['1'].naCount,
	// 		controlsSetSize: igScores['1'].controls.size
	// 	},
	// 	'2': { 
	// 		total: igScores['2'].total, 
	// 		count: igScores['2'].count, 
	// 		applicableCount: igScores['2'].applicableCount,
	// 		applicableControlsCount: igScores['2'].applicableControls.size,
	// 		unansweredCount: igScores['2'].unansweredCount,
	// 		naCount: igScores['2'].naCount,
	// 		controlsSetSize: igScores['2'].controls.size
	// 	},
	// 	'3': { 
	// 		total: igScores['3'].total, 
	// 		count: igScores['3'].count, 
	// 		applicableCount: igScores['3'].applicableCount,
	// 		applicableControlsCount: igScores['3'].applicableControls.size,
	// 		unansweredCount: igScores['3'].unansweredCount,
	// 		naCount: igScores['3'].naCount,
	// 		controlsSetSize: igScores['3'].controls.size
	// 	}
	// });

	// Calculate averages for each implementation group
	// The denominator should be the total possible controls from standards (minus NA items)
	// Unanswered controls count as 0 in the numerator
	// This ensures:
	// - If 10 controls answered with max (1.0 each): score = (10 / 56) * 5 = 0.89 (not 5.0)
	// - If all 56 controls answered with max (1.0 each): score = (56 / 56) * 5 = 5.0
	const averages = {};
	Object.keys(igScores).forEach(ig => {
		const { total, applicableControls, applicableCount, unansweredCount, naCount } = igScores[ig];
		
		// Denominator is the total possible controls from standards minus NA items
		// This includes both answered and unanswered controls
		const totalPossibleControls = applicableCount > 0 ? (applicableCount - naCount) : applicableControls.size;
		
		// Numerator is the sum of maturity scores (0-1 scale) for answered controls
		// Unanswered controls contribute 0, which is already accounted for (they're not in total)
		// So: total already represents the sum of answered controls' scores
		// No need to add (unansweredCount * 0) since it's already 0
		
		// Calculate score - using total possible controls as denominator
		// If all controls are answered with max (1.0): total = applicableCount * 1.0, score = 5.0
		// If only some controls answered: total < applicableCount, so score < 5.0
		const score = totalPossibleControls > 0 ? (total / totalPossibleControls) * 5 : 0;
		// console.log(`Calculating score for IG${ig}:`, {
		// 	total,
		// 	totalPossibleControls,
		// 	applicableCountFromStandards: applicableCount,
		// 	naCount,
		// 	applicableControlsObserved: applicableControls.size,
		// 	unansweredCount,
		// 	calculation: `(${total} / ${totalPossibleControls}) * 5 = ${score}`,
		// 	explanation: `total is sum of ${applicableControls.size} answered controls (0-1 scale). ${unansweredCount} unanswered controls count as 0. Denominator is ${totalPossibleControls} total possible controls (minus ${naCount} NA).`
		// });
		averages[ig] = score;
	});

	// console.log('DEBUG - Final Scores:', averages);
	return averages;
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
	module.exports = calculateImplementationGroupScores;
} else if (typeof window !== 'undefined') {
	window.calculateImplementationGroupScores = calculateImplementationGroupScores;
}
